const w = window

class Instance {
  constructor() {
    if (!w.MissiveChatConfig || !w.MissiveChatConfig.id) {
      return console.warn('Invalid Missive Chat configuration')
    }

    let { user } = w.MissiveChatConfig
    if (user && user.digest && user.digest.match(/[^\x00-\x7F]/)) {
      // Ignore non-ASCII digest
      user.digest = undefined
    }

    this.DOMNodes = { html: document.documentElement, body: document.body }
    this.options = { domain: document.domain }
    this.config = this.mergeConfigs()

    this.injectHTML()

    this.handleMessage = this.handleMessage.bind(this)
    w.addEventListener('message', this.handleMessage)
  }

  destroy() {
    const { body, main } = this.DOMNodes
    body.removeChild(main)

    w.removeEventListener('message', this.handleMessage)
  }

  sendMessage(attrs) {
    const { iframe } = this.DOMNodes
    if (!iframe || !iframe.contentWindow) return

    try {
      iframe.contentWindow.postMessage(
        attrs,
        new URL(iframe.src, window.location.href).origin,
      )
    } catch (e) {}
  }

  handleCallback({ callback, data }) {
    w.MissiveChatConfig[callback] && w.MissiveChatConfig[callback](data)
  }

  load() {
    if (this.loaded) return
    this.loaded = true

    const { main } = this.DOMNodes
    main.classList.add('__loaded')

    w.MissiveChatConfig.onReady &&
      setTimeout(() => {
        w.MissiveChatConfig.onReady()
      }, this.options.transitionDuration)
  }

  setUser(user) {
    user = clone(user)

    w.MissiveChatConfig.user = user
    this.exec('reset', { user })
  }

  getMeta() {
    const meta = {
      href: location.href.replace(/\/$/, ''),
      user_agent: navigator.userAgent,
      language: (navigator.language || navigator.userLanguage || '')
        .split('-')[0]
        .toLowerCase(),
    }

    if (w.MissiveChatConfig.meta) {
      meta.custom = w.MissiveChatConfig.meta
    }

    return meta
  }

  setMeta(data) {
    if (!isObject(data)) return
    w.MissiveChatConfig.meta = clone(data) || undefined
  }

  addMeta(data) {
    if (!isObject(data)) return
    w.MissiveChatConfig.meta || (w.MissiveChatConfig.meta = {})

    for (let k in data) {
      w.MissiveChatConfig.meta[k] = data[k]
    }
  }

  setVariables(variables) {
    if (!isObject(variables)) return

    for (let k in variables) {
      w.MissiveChatConfig.chat.variables[k] = variables[k]
      this.config.chat.variables[k] = variables[k]
    }

    this.injectDynamicStyles({ reset: true })
    this.exec('handleUpdate', { config: this.config })
  }

  mergeConfigs(serverConfig) {
    const config = deepMerge(
      {},
      DEFAULT_CONFIG,
      serverConfig,
      w.MissiveChatConfig,
    )

    if (config.support.email || config.support.phone) {
      if (!Array.isArray(config.messages.error)) {
        config.messages.error = [config.messages.error]
      }

      config.messages.error.push(
        [
          'You can also',
          config.support.email
            ? `send us an email at ${config.support.email}`
            : '',
          config.support.email && config.support.phone ? 'or' : '',
          config.support.phone
            ? `reach out by phone at {{ tel:${config.support.phone} }}`
            : '',
        ]
          .join(' ')
          .trim(),
      )
    }

    return config
  }

  handleServerConfig(serverConfig) {
    this.config = this.mergeConfigs(serverConfig)

    this.injectDynamicStyles()
    this.exec('handleUpdate', { config: this.config, options: this.options })
  }

  exec(method, params) {
    this.sendMessage({
      event: 'missive-webchat-exec',
      data: { method, params },
    })
  }

  open() {
    this.options.opened = true

    const { html, main, wrapper, iframe } = this.DOMNodes

    main.classList.remove('__closed')
    main.classList.add('__opened')
    main.onclick = null

    let openConstraint = () => {
      this.pageXOffset = undefined
      this.pageYOffset = undefined
    }

    let openFullscreen = () => {
      this.pageXOffset = w.pageXOffset
      this.pageYOffset = w.pageYOffset

      main.setAttribute('data-fullscreen', true)

      setTimeout(() => {
        html.classList.add('missive-chat-fullscreen')
      }, this.options.transitionDuration + 100)
    }

    let sizes = [w.innerWidth, w.innerHeight].sort((a, b) => a - b)
    sizes[0] <= 500 && sizes[1] <= 926 ? openFullscreen() : openConstraint()

    w.MissiveChatConfig.onOpen && w.MissiveChatConfig.onOpen()
  }

  close({ silent } = {}) {
    this.options.opened = false

    const { html, main } = this.DOMNodes

    if (this.pageYOffset != undefined) {
      html.classList.remove('missive-chat-fullscreen')
      w.scrollTo(this.pageXOffset, this.pageYOffset)
    }

    main.classList.add('__closed')
    main.classList.remove('__opened')
    main.removeAttribute('data-fullscreen')

    main.onclick = () => {
      this.exec('open')
    }

    if (!silent) {
      w.MissiveChatConfig.onClose && w.MissiveChatConfig.onClose()
    }
  }

  showButton() {
    this.options.hidden = false

    const { main } = this.DOMNodes
    main.classList.remove('__hidden')

    this.exec('handleUpdate', { options: this.options })
  }

  hideButton() {
    this.options.hidden = true

    const { main } = this.DOMNodes
    main.classList.add('__hidden')

    this.exec('handleUpdate', { options: this.options })
  }

  setBadge(count) {
    let badge = this.DOMNodes.badge

    if (!count) {
      badge.classList.remove('__visible')
      this.clearNewMessageTitle()
    } else {
      badge.textContent = count
      badge.classList.add('__visible')
      this.showNewMessageTitle(count)
    }
  }

  clearNewMessageTitle() {
    clearInterval(this.newMessageTitleInterval)

    if (this.originalTitle != undefined) {
      document.title = this.originalTitle || ''
    }
  }

  showNewMessageTitle(count) {
    if (this.originalTitle == undefined) {
      this.originalTitle = document.title
    }

    let a = () => {
      document.title = `(${count}) ${
        count > 1
          ? this.config.messages.newMessages
          : this.config.messages.newMessage
      }`
    }

    let b = () => {
      document.title = this.originalTitle || ''
    }

    this.clearNewMessageTitle()
    a()

    let even = false
    this.newMessageTitleInterval = setInterval(() => {
      even ? a() : b()
      even = !even
    }, 1000)
  }

  handleMessage(e) {
    const { iframe } = this.DOMNodes
    if (
      !iframe ||
      e.source !== iframe.contentWindow ||
      e.origin !== new URL(iframe.src, window.location.href).origin ||
      !e.data ||
      typeof e.data !== 'object'
    ) {
      return
    }

    let { messageId, event, data } = e.data

    switch (event) {
      case 'missive-webchat-init':
        this.sendMessage({
          event: 'missive-webchat-init',
          data: { config: this.config, options: this.options },
        })
        break
      case 'missive-webchat-close':
        this.close()
        break
      case 'missive-webchat-open':
        this.open()
        break
      case 'missive-webchat-destroy':
        this.destroy()
        break
      case 'missive-webchat-loaded':
        this.load()
        break
      case 'missive-webchat-badge':
        this.setBadge(data)
        break
      case 'missive-webchat-config':
        this.handleServerConfig(data)
        break
      case 'missive-webchat-callback':
        this.handleCallback(data)
        break
      case 'missive-webchat-meta':
        const meta = this.getMeta()

        this.sendMessage({
          messageId,
          data: meta,
        })

        break
    }
  }

  injectHTML() {
    const { html, body } = this.DOMNodes

    let main = document.createElement('div')
    main.setAttribute('id', 'missive-webchat')

    let wrapper = document.createElement('div')
    wrapper.setAttribute('id', 'missive-webchat-wrapper')

    let badge = document.createElement('div')
    badge.setAttribute('id', 'missive-webchat-badge')

    let iframe = document.createElement('iframe')
    iframe.setAttribute('id', 'missive-webchat-iframe')
    iframe.setAttribute('title', this.config.messages.title)
    iframe.setAttribute('allowTransparency', 'true')
    iframe.setAttribute('frameBorder', '0')
    iframe.setAttribute(
      'src',
      `${process.env.WEBCHAT_HOST || ''}/${this.config.id}/webchat`,
    )

    let style = document.createElement('style')
    style.type = 'text/css'

    // prettier-ignore
    style.textContent = generateImportantCSS([
      [
        'html.missive-chat-fullscreen, html.missive-chat-fullscreen > body',
        [
          'position: static',
          'overflow: hidden',
        ],
      ],
      [
        'html.missive-chat-fullscreen > body',
        [
          'height: 0',
          'margin: 0',
        ]
      ],

      [
        '#missive-webchat, #missive-webchat *',
        ['-webkit-tap-highlight-color: rgba(0,0,0, 0)'],
      ],

      [
        '#missive-webchat',
        [
          'opacity: 0',
          'pointer-events: none',
          'position: fixed',
          'max-width: calc(100% - 40px)',
          'max-height: calc(100% - 40px)',
          'min-width: 0',
          'min-height: 0',
          'display: block',
          'box-sizing: content-box',
          'margin: 0',
          'padding: 0',
          'width: 62px',
          'height: 62px',
          'transform: scale(0) rotate(-20deg)',
          'z-index: 2147483001',
        ],
      ],

      [
        '#missive-webchat[data-position="top-right"]',
        ['bottom: auto', 'left: auto'],
      ],
      [
        '#missive-webchat[data-position="top-right"][data-fullscreen]',
        [
          'top: 0', 'right: 0',
          'bottom: auto', 'left: auto',
        ],
      ],
      [
        '#missive-webchat[data-position="top-left"]',
        ['bottom: auto', 'right: auto'],
      ],
      [
        '#missive-webchat[data-position="top-left"][data-fullscreen]',
        [
          'top: 0', 'left: 0',
          'bottom: auto', 'right: auto',
        ],
      ],
      [
        '#missive-webchat[data-position="bottom-left"]',
        ['top: auto', 'right: auto'],
      ],
      [
        '#missive-webchat[data-position="bottom-left"][data-fullscreen]',
        [
          'bottom: 0', 'left: 0',
          'top: auto', 'right: auto',
        ],
      ],

      [
        '#missive-webchat.__loaded',
        [
          'opacity: 1',
          'transform: scale(1) rotate(0)',
          'pointer-events: auto'
        ],
      ],

      [
        '#missive-webchat.__closed',
        ['cursor: pointer']
      ],
      [
        '#missive-webchat.__closed.__hidden',
        ['transform: scale(0)']
      ],
      [
        '#missive-webchat.__closed:active',
        ['transform: scale(.92)']
      ],
      [
        '#missive-webchat.__closed #missive-webchat-iframe',
        ['pointer-events: none']
      ],

      [
        '#missive-webchat[data-fullscreen].__opened',
        [
          'width: 100%', 'height: 100%',
          'max-width: none', 'max-height: none',
          'bottom: 0', 'right: 0',
        ],
      ],
      [
        '#missive-webchat[data-fullscreen].__opened #missive-webchat-wrapper',
        [
          'border: 0',
          'border-radius: 0'
        ],
      ],

      [
        '#missive-webchat-wrapper',
        [
          'overflow: hidden',
          'position: absolute',
          'width: 100%', 'height: 100%',
          'min-width: 100%', 'max-width: 100%',
          'min-height: 100%', 'max-height: 100%',
          'top: 0', 'left: 0',
          'margin: 0', 'padding: 0',
          'display: block',
          'border-radius: 60px',
          'box-sizing: border-box',
          'background: transparent',
        ],
      ],

      [
        '#missive-webchat-badge',
        [
          'position: absolute',
          'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          'text-align: center',
          'top: -6px',
          'right: -6px',
          'width: 24px',
          'height: 24px',
          'line-height: 24px',
          'font-size: 14px',
          'transform: scale(0)',
        ],
      ],
      [
        '#missive-webchat-badge.__visible',
        ['transform: scale(1)']
      ],

      [
        '#missive-webchat-iframe',
        [
          'height: 100%',
          'width: 100%',
          'min-width: 100%',
          'max-width: 100%',
          'min-height: 100%',
          'max-height: 100%',
          'margin: 0',
          'background-color: transparent',
        ],
      ],
    ])

    wrapper.appendChild(iframe)
    main.appendChild(wrapper)
    main.appendChild(badge)
    main.appendChild(style)

    this.DOMNodes = { html, body, main, wrapper, badge, iframe }
    this.close({ silent: true })

    body.appendChild(main)
  }

  injectDynamicStyles({ reset } = {}) {
    const { main } = this.DOMNodes

    if (reset && this.DOMNodes.dynamicStyle) {
      main.removeChild(this.DOMNodes.dynamicStyle)
      delete this.DOMNodes.dynamicStyle
    }

    if (this.DOMNodes.dynamicStyle) return

    main.setAttribute('data-position', this.config.chat.position)
    if (this.config.chat.hidden) this.hideButton()

    let transitionDuration = this.getCSSVariable('transition-duration')
    let duration = parseFloat(transitionDuration) || 0
    if (transitionDuration.indexOf('ms') == -1) {
      duration *= 1000
    }
    this.options.transitionDuration = duration

    let dynamicStyle = document.createElement('style')
    dynamicStyle.type = 'text/css'

    // prettier-ignore
    dynamicStyle.textContent = generateImportantCSS([
      [
        '#missive-webchat',
        [
          `bottom: ${this.getCSSVariable('margin-bottom', 'margin')}`,
          `bottom: calc(${this.getCSSVariable('margin-bottom', 'margin')} + env(safe-area-inset-bottom))`,
          `right: ${this.getCSSVariable('margin-right', 'margin')}`,
          `transition-property: transform, opacity, width, height`,
          `transition-duration: ${this.getCSSVariable('transition-duration')}`,
          `transition-timing-function: ${this.getCSSVariable('transition-easing')}`,
        ],
      ],

      [
        '#missive-webchat.__hidden',
        [`transform-origin: ${this.config.chat.position.replace('-', ' ')}`]
      ],

      [
        '#missive-webchat[data-position="top-right"]',
        [
          `top: ${this.getCSSVariable('margin-top', 'margin')}`,
          `right: ${this.getCSSVariable('margin-right', 'margin')}`,
        ],
      ],
      [
        '#missive-webchat[data-position="top-left"]',
        [
          `top: ${this.getCSSVariable('margin-top', 'margin')}`,
          `left: ${this.getCSSVariable('margin-left', 'margin')}`,
        ],
      ],
      [
        '#missive-webchat[data-position="bottom-left"]',
        [
          `bottom: ${this.getCSSVariable('margin-bottom', 'margin')}`,
          `bottom: calc(${this.getCSSVariable('margin-bottom', 'margin')} + env(safe-area-inset-bottom))`,
          `left: ${this.getCSSVariable('margin-left', 'margin')}`,
        ],
      ],

      [
        '#missive-webchat.__opened',
        [
          `width: ${this.getCSSVariable('width')}`,
          `height: ${this.getCSSVariable('height')}`,
          `max-height: ${this.getCSSVariable('max-height')}`,
          `max-width: ${this.getCSSVariable('max-width')}`,
        ],
      ],
      [
        '#missive-webchat.__opened #missive-webchat-wrapper',
        [`border-radius: ${this.getCSSVariable('opened-border-radius')}`],
      ],

      [
        '#missive-webchat-wrapper',
        [
          `border-radius: ${this.getCSSVariable('closed-border-radius')}`,
          `box-shadow: ${this.getCSSVariable('box-shadow')}`,
          `transition: all ${this.getCSSVariable('transition-duration')} ${this.getCSSVariable('transition-easing')}`,
        ],
      ],

      [
        '#missive-webchat-badge',
        [
          `color: ${this.getCSSVariable('badge-color')}`,
          `background-color: ${this.getCSSVariable('badge-background-color')}`,
          `border-radius: ${this.getCSSVariable('badge-border-radius')}`,
          `box-shadow: ${this.getCSSVariable('badge-box-shadow')}`,
          `transition: all ${this.getCSSVariable('transition-duration')} ${this.getCSSVariable('transition-easing')}`,
        ],
      ],
    ])

    this.DOMNodes.dynamicStyle = dynamicStyle
    main.appendChild(dynamicStyle)
  }

  getCSSVariable(...keys) {
    for (let key of keys) {
      let value = this.config.chat.variables[key]
      if (value) {
        return value
      }
    }
  }
}

// prettier-ignore
const DEFAULT_CONFIG = {
  chat: {
    position: 'bottom-right',
    hidden: false,
    hideWhenOffline: false,
    hideBranding: false,
    showOnlineStatus: true,
    showAvatars: true,
    showNames: true,
    rememberOpenState: true,
    variables: {
      'background-color': '#fff',
      'badge-background-color': '#f23131',
      'badge-border-radius': '100%',
      'badge-box-shadow': '0 1px 2px rgba(0,0,0, .2)',
      'badge-color': '#fff',
      'badge-size': '24px',
      'border-color': 'rgba(0,0,0, .05)',
      'box-shadow': '0 5px 30px rgba(0,0,0, .1)',
      'bubble-dark-color': 'var(--main-color)',
      'bubble-light-color': '#f5f5f5',
      'button-border-radius': '10em',
      'close-button-color': '#fff',
      'closed-border-radius': '30px',
      'font-family': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
      'font-size': '15px',
      'header-opened-background-color': 'var(--main-color)',
      'header-subtitle-text-color': 'rgba(255,255,255, .65)',
      'header-title-text-color': '#fff',
      'height': '600px',
      'light-text-color': '#7b7c7f',
      'main-color': '#2866D1',
      'margin': '20px',
      'max-height': 'calc(100% - 40px)',
      'max-width': 'calc(100% - 40px)',
      'opened-border-radius': '10px',
      'status-connecting-color': '#fdd157',
      'status-offline-color': '#f23131',
      'status-online-color': '#00b351',
      'text-color': '#34363a',
      'transition-duration': '225ms',
      'transition-easing': 'cubic-bezier(.4, .0, .2, 1)',
      'width': '350px',
    }
  },
  support: {},
  user: {},
  visitor: {
    askName: true,
    requireName: false,
    askEmail: true,
    requireEmail: true,
    requireConsent: false,
  },
  messages: {
    welcome: 'Hi there! 👋\n\nThe team will reply as soon as possible.',
    help: 'How can we help?',
    title: 'Chat with us',
    online: 'We’re online',
    offline: 'We’re offline',
    connecting: 'Connecting…',
    chat: 'Message…',
    newMessage: 'New message!',
    newMessages: 'New messages!',
    error: 'Sorry, there is an issue connecting to our chat. Please check your Internet connection and try again.',
    internet: 'Can’t send messages offline.',
    identify: 'First, identify yourself.',
    identifyName: 'Name',
    identifyEmail: 'Email',
    identifySubmit: 'Submit',
    identifySkip: 'Skip',
    identifyRequired: 'Required field',
    identifyThanks: 'Thanks',
    identifyConsent: 'I accept this website privacy policy and terms of use.',
  },
}

const generateImportantCSS = (selectors) => {
  return selectors
    .map(([selector, attrs]) => {
      return `${selector} { ${
        attrs ? attrs.concat('').join(' !important; ') : ''
      }}`
    })
    .join('\n')
}

const clone = (o) => {
  return JSON.parse(JSON.stringify(o))
}

const isObject = (o) => {
  return typeof o == 'object' && !Array.isArray(o)
}

const deepMerge = (target, ...sources) => {
  for (let source of sources) {
    let s = null

    try {
      s = clone(source)
    } catch (e) {
      continue
    }

    for (let k in s) {
      const v = s[k]

      if (isObject(v) && k in target) {
        deepMerge(target[k], v)
      } else {
        target[k] = v
      }
    }
  }

  return target
}

;(() => {
  w.MissiveChat = {
    open: function() {
      this.instance && this.instance.exec('open')
    },

    close: function() {
      this.instance && this.instance.exec('close')
    },

    showButton: function() {
      this.instance && this.instance.showButton()
    },

    hideButton: function() {
      this.instance && this.instance.hideButton()
    },

    setUser: function(user) {
      this.instance && this.instance.setUser(user)
    },

    setMeta: function(data) {
      this.instance && this.instance.setMeta(data)
    },

    addMeta: function(data) {
      this.instance && this.instance.addMeta(data)
    },

    setVariables: function(data) {
      this.instance && this.instance.setVariables(data)
    },
  }

  w.MissiveChat.show = w.MissiveChat.showButton
  w.MissiveChat.hide = w.MissiveChat.hideButton

  if (!('Intl' in window || 'classList' in Element.prototype)) {
    return console.warn(`Missive Chat isn’t compatible with this browser.`)
  }

  const init = () => {
    if (w.MissiveChat.instance) {
      w.MissiveChat.instance.destroy()
    }

    w.MissiveChat.instance = new Instance()
  }

  // Turbolinks
  window.addEventListener('page:update', init)

  if (document.readyState == 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
